import { useI18n } from "@core/i18n/i18n";
import { useQueryParams } from "@core/routing/use-query-params";
import { Error404 } from "@ui/components/common/error";
import { LoadingContainer } from "@ui/components/common/loading-container";
import { RoutesPath } from "@ui/navigation/routes";
import { useCountryConfig } from "@ui/pages/patient-details/hooks/use-country-config";
import { InviteYourPtForm } from "@ui/pages/prospect/campaigns/invite-your-pt/invite-your-pt-form";
import { useProspectRequestLoading } from "@ui/pages/prospect/hooks/use-prospect-request-loading";
import { ProspectFormContainer } from "@ui/pages/prospect/prospect-form-container";
import { ProspectPage } from "@ui/pages/prospect/prospect-page";
import { ProspectSuccessMessage } from "@ui/pages/prospect/prospect-success-message";
import { colors } from "@ui/style/colors";
import { isNil } from "@utils/assert";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { localeManager } from "@core/service-container";

export const InviteYourPtPage = (): JSX.Element => {
	const { format } = useI18n();
	const { locale: queryLocale } = useQueryParams<RoutesPath.InviteYourPt>();
	const locale = useMemo(
		() => (queryLocale ? localeManager.resolveLocale(queryLocale) : localeManager.resolveLocaleFromNavigator()),
		[queryLocale],
	);
	const { config, configError, configLoading } = useProspectRequestLoading(locale);
	const [isFormSent, setFormSent] = useState(false);
	const { countryConfig } = useCountryConfig();

	useEffect(() => {
		localeManager.changeLocale(locale);
	}, [locale]);

	const navigateToWebsite = useCallback(() => {
		window.open(countryConfig?.landingPages.patient, "_self");
	}, [countryConfig?.landingPages.patient]);

	let formContent: JSX.Element;
	if (configLoading) {
		formContent = <LoadingContainer />;
	} else if (configError || isNil(config)) {
		/* TODO: MAR-1614 Change 404 to a more suitable error */
		formContent = <Error404 />;
	} else {
		formContent = <InviteYourPtForm config={config} onSuccess={() => setFormSent(true)} />;
	}

	return (
		<ProspectPage backgroundColor={colors.mobilePrimaryBlue}>
			{!isFormSent ? (
				<ProspectFormContainer
					title={format("inviteYourPt.form.title", { color: colors.mobilePrimaryBlue })}
					description={format("inviteYourPt.form.description")}
				>
					<div>{formContent}</div>
				</ProspectFormContainer>
			) : (
				<ProspectSuccessMessage
					title={format("inviteYourPt.confirmMessage.title")}
					description={format("inviteYourPt.confirmMessage.description")}
					buttonLabel={format("inviteYourPt.confirmMessage.backButton")}
					onButtonClick={navigateToWebsite}
				/>
			)}
		</ProspectPage>
	);
};
